import { Modal, ModalTitle, ModalBody } from 'design-system/src/components/Modal';
import { useRef, useState } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { SearchInput } from '../Table';
import styled from 'styled-components';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow';
import { useMutation } from '@tanstack/react-query';
import { client, Address } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: auto;
  gap: 0.5rem;
`;

const libraries = ['places'];

type PharmacyAddress = {
  name: string;
  address: Address;
  formatted_address: string;
  phone_number: string;
  google_place_id: string;
  latitude: number;
  longitude: number;
};

export default function PharmacySearchModal({
  onClose,
  onSubmit,
  rxId,
}: {
  onClose: () => void;
  onSubmit: () => void;
  rxId: string;
}) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const placesDivRef = useRef<HTMLDivElement | null>(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState<PharmacyAddress | null>(null);

  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_CLIENT_KEY || '';

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries as any,
  });

  const extractAddress = (addressComponents: any[]): Address => {
    const getComponent = (type: string) => {
      const comp = addressComponents.find((c: any) => c.types.includes(type));
      return comp ? comp.long_name : '';
    };
    return {
      line1: getComponent('street_number')
        ? `${getComponent('street_number')} ${getComponent('route')}`.trim()
        : getComponent('route'),
      line2: '',
      city: getComponent('locality') || getComponent('sublocality') || getComponent('postal_town'),
      state: getComponent('administrative_area_level_1'),
      postal_code: getComponent('postal_code'),
    };
  };

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (place && place.place_id) {
      const { maps } = (window as any).google;
      const service = new maps.places.PlacesService(placesDivRef.current || document.createElement('div'));
      service.getDetails(
        {
          placeId: place.place_id,
          fields: ['name', 'formatted_address', 'address_components', 'formatted_phone_number', 'place_id', 'geometry'],
        },
        (details: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && details && details.address_components) {
            const address = extractAddress(details.address_components || []);
            setSelectedPharmacy({
              name: details.name || '',
              address,
              formatted_address: details.formatted_address || '',
              phone_number: details.formatted_phone_number || '',
              google_place_id: details.place_id || '',
              latitude: details.geometry?.location?.lat() || 0,
              longitude: details.geometry?.location?.lng() || 0,
            });
          }
        },
      );
    }
  };

  const updatePharmacyMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPharmacy) {
        Sentry.captureException('No pharmacy selected');
        return;
      }
      const payload = {
        name: selectedPharmacy.name,
        address_line_1: selectedPharmacy.address.line1,
        formatted_address: selectedPharmacy.formatted_address,
        city: selectedPharmacy.address.city,
        state: selectedPharmacy.address.state,
        zip_code: selectedPharmacy.address.postal_code,
        phone_number: selectedPharmacy.phone_number,
        google_place_id: selectedPharmacy.google_place_id,
        latitude: selectedPharmacy.latitude,
        longitude: selectedPharmacy.longitude,
      };
      return client.post(`/rxs/${rxId}/preferred-pharmacy`, payload).then((res) => res.data);
    },
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Pharmacy updated</Toast>);
      onSubmit();
      onClose();
    },
    onError: () => {
      toast.custom(() => <Toast variant="error">Failed to update pharmacy</Toast>);
    },
  });

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Select patient's preferred pharmacy</ModalTitle>
      <ModalBody>
        {isLoaded && (
          <Autocomplete
            onLoad={(ref) => {
              autocompleteRef.current = ref;
            }}
            onPlaceChanged={handlePlaceChanged}
            options={{
              types: ['establishment'],
              componentRestrictions: { country: 'us' },
              fields: ['name', 'formatted_address', 'geometry', 'place_id'],
            }}
          >
            <SearchContainer>
              <SearchInput label="Search for a pharmacy" placeholder="Address" $width="100%" hideLabel={true} />
            </SearchContainer>
          </Autocomplete>
        )}
        {/* This is a hidden div that is used to store the places service */}
        <div ref={placesDivRef} style={{ display: 'none' }} />
        {selectedPharmacy && (
          <InfoBox includeIcon={false}>
            {selectedPharmacy.name && <Typography weight={600}>{selectedPharmacy.name}</Typography>}
            <Typography>{selectedPharmacy.address.line1}</Typography>
            <Typography>
              {selectedPharmacy.address.city}, {selectedPharmacy.address.state} {selectedPharmacy.address.postal_code}
            </Typography>
            {selectedPharmacy.phone_number && <Typography>{selectedPharmacy.phone_number}</Typography>}
          </InfoBox>
        )}
        <ButtonRow>
          <Button variant="tertiary" onClick={onClose} disabled={updatePharmacyMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => updatePharmacyMutation.mutate()}
            disabled={!selectedPharmacy}
            isLoading={updatePharmacyMutation.isPending}
          >
            Confirm pharmacy
          </Button>
        </ButtonRow>
      </ModalBody>
    </Modal>
  );
}
